/**
 * MetricsCard Component
 *
 * Display a single metric with label, value, and optional trend/icon.
 * Used in the Classification Metrics dashboard.
 */

import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';

export interface MetricsCardProps {
  /** The metric label */
  label: string;
  /** The metric value (formatted string) */
  value: string;
  /** Optional description or subtitle */
  description?: string;
  /** Optional icon component */
  icon?: LucideIcon;
  /** Icon color variant */
  iconColor?: 'gray' | 'blue' | 'green' | 'amber' | 'purple' | 'red';
  /** Whether this is a highlighted/primary metric */
  highlight?: boolean;
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
}

const iconColorClasses: Record<string, string> = {
  gray: 'text-gray-500 bg-gray-100',
  blue: 'text-blue-600 bg-blue-100',
  green: 'text-green-600 bg-green-100',
  amber: 'text-amber-600 bg-amber-100',
  purple: 'text-purple-600 bg-purple-100',
  red: 'text-red-600 bg-red-100',
};

/**
 * MetricsCard displays a single metric in a card format.
 *
 * @example
 * <MetricsCard
 *   label="Automation Rate"
 *   value="75%"
 *   icon={Zap}
 *   iconColor="green"
 *   highlight
 * />
 */
export function MetricsCard({
  label,
  value,
  description,
  icon: Icon,
  iconColor = 'gray',
  highlight = false,
  size = 'md',
}: MetricsCardProps) {
  return (
    <div
      className={clsx(
        'bg-white rounded-lg border overflow-hidden',
        highlight ? 'border-blue-200 ring-1 ring-blue-100' : 'border-gray-200',
        {
          'p-3': size === 'sm',
          'p-4': size === 'md',
          'p-5': size === 'lg',
        }
      )}
      data-testid="metrics-card"
    >
      <div className="flex items-start gap-3">
        {Icon && (
          <div
            className={clsx(
              'rounded-lg p-2',
              iconColorClasses[iconColor]
            )}
          >
            <Icon size={size === 'lg' ? 24 : size === 'sm' ? 16 : 20} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p
            className={clsx(
              'font-medium text-gray-500 uppercase tracking-wider',
              {
                'text-xs': size === 'sm' || size === 'md',
                'text-sm': size === 'lg',
              }
            )}
          >
            {label}
          </p>
          <p
            className={clsx(
              'font-bold text-gray-900 mt-1',
              {
                'text-xl': size === 'sm',
                'text-2xl': size === 'md',
                'text-3xl': size === 'lg',
              }
            )}
            data-testid="metrics-value"
          >
            {value}
          </p>
          {description && (
            <p
              className={clsx(
                'text-gray-500 mt-1',
                {
                  'text-xs': size === 'sm',
                  'text-sm': size === 'md' || size === 'lg',
                }
              )}
            >
              {description}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default MetricsCard;
